
import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle } from "lucide-react";
import { toast } from "sonner";

// Définition du schéma de validation avec Zod
const formSchema = z.object({
  fullName: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  company: z.string().min(1, { message: "Company name is required" }),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().optional(),
  message: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const DemoRequestForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      company: "",
      phoneNumber: "",
      jobTitle: "",
      message: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    
    // Simuler une API call
    try {
      // Dans un environnement réel, remplacer par un vrai appel API
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      console.log("Form data submitted:", data);
      setIsSubmitted(true);
      reset();
      toast.success("Your demo request has been submitted successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("There was an error submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white dark:bg-benchr-900/30 rounded-xl shadow-lg overflow-hidden">
      <div className="p-8 md:p-10">
        {!isSubmitted ? (
          <>
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-benchr-900 dark:text-white mb-3">
                Request a Personalized Demo
              </h2>
              <p className="text-benchr-600 dark:text-benchr-300">
                See how benchr.ai can help your business gain a competitive edge in the e-commerce landscape.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-benchr-700 dark:text-benchr-300 mb-1.5">
                    Full Name*
                  </label>
                  <input 
                    type="text"
                    className={`w-full px-4 py-2.5 rounded-lg border ${
                      errors.fullName 
                        ? "border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-900/10" 
                        : "border-benchr-200 dark:border-benchr-700 bg-white dark:bg-benchr-800/50"
                    } focus:outline-none focus:ring-2 focus:ring-benchr-500/50 dark:focus:ring-benchr-400/30 transition-colors`}
                    placeholder="John Doe"
                    {...register("fullName")}
                  />
                  {errors.fullName && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.fullName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-benchr-700 dark:text-benchr-300 mb-1.5">
                    Business Email*
                  </label>
                  <input 
                    type="email"
                    className={`w-full px-4 py-2.5 rounded-lg border ${
                      errors.email 
                        ? "border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-900/10" 
                        : "border-benchr-200 dark:border-benchr-700 bg-white dark:bg-benchr-800/50"
                    } focus:outline-none focus:ring-2 focus:ring-benchr-500/50 dark:focus:ring-benchr-400/30 transition-colors`}
                    placeholder="john.doe@company.com"
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-benchr-700 dark:text-benchr-300 mb-1.5">
                    Company*
                  </label>
                  <input 
                    type="text"
                    className={`w-full px-4 py-2.5 rounded-lg border ${
                      errors.company 
                        ? "border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-900/10" 
                        : "border-benchr-200 dark:border-benchr-700 bg-white dark:bg-benchr-800/50"
                    } focus:outline-none focus:ring-2 focus:ring-benchr-500/50 dark:focus:ring-benchr-400/30 transition-colors`}
                    placeholder="Company Inc."
                    {...register("company")}
                  />
                  {errors.company && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.company.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-benchr-700 dark:text-benchr-300 mb-1.5">
                    Job Title
                  </label>
                  <input 
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg border border-benchr-200 dark:border-benchr-700 bg-white dark:bg-benchr-800/50 focus:outline-none focus:ring-2 focus:ring-benchr-500/50 dark:focus:ring-benchr-400/30 transition-colors"
                    placeholder="Marketing Manager"
                    {...register("jobTitle")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-benchr-700 dark:text-benchr-300 mb-1.5">
                    Phone Number
                  </label>
                  <input 
                    type="tel"
                    className="w-full px-4 py-2.5 rounded-lg border border-benchr-200 dark:border-benchr-700 bg-white dark:bg-benchr-800/50 focus:outline-none focus:ring-2 focus:ring-benchr-500/50 dark:focus:ring-benchr-400/30 transition-colors"
                    placeholder="+1 (555) 123-4567"
                    {...register("phoneNumber")}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-benchr-700 dark:text-benchr-300 mb-1.5">
                    Additional Information
                  </label>
                  <textarea 
                    className="w-full px-4 py-2.5 rounded-lg border border-benchr-200 dark:border-benchr-700 bg-white dark:bg-benchr-800/50 focus:outline-none focus:ring-2 focus:ring-benchr-500/50 dark:focus:ring-benchr-400/30 transition-colors min-h-[100px]"
                    placeholder="Tell us about your specific needs or questions..."
                    {...register("message")}
                  />
                </div>
              </div>

              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center px-6 py-3 bg-benchr-700 hover:bg-benchr-800 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-300 ease-in-out disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      Request Demo
                      <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center py-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-6">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-benchr-900 dark:text-white mb-3">
              Thank You!
            </h2>
            <p className="text-benchr-600 dark:text-benchr-300 max-w-md mx-auto mb-6">
              Your demo request has been submitted successfully. Our team will contact you shortly to schedule your personalized demo.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="inline-flex items-center justify-center px-6 py-2.5 bg-benchr-50 dark:bg-benchr-800/60 hover:bg-benchr-100 dark:hover:bg-benchr-800 text-benchr-700 dark:text-white font-medium rounded-lg border border-benchr-200 dark:border-benchr-700/50 transition-colors duration-300"
            >
              Request Another Demo
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRequestForm;
